import { getLabel } from '../../utils/labelStore';

/**
 * Report-only column sets. They intentionally do NOT reuse debtsColumns /
 * debtDuesColumns / creditDuesColumns from utils.js: those still describe the
 * legacy on-screen tables (Predictions) and must keep their current shape
 * (symbol-prefixed amounts, hidden Deudor and Moneda columns).
 *
 * Differences from the shared sets:
 *  - the Deudor/Acreedor column holds the account name (owner.name), not the
 *    Cuenta:Concepto code, and is exported rather than hidden;
 *  - money columns are `amount` (plain number) instead of `currency`
 *    (symbol-prefixed text), so the sheet can be totalled in Excel;
 *  - 'Moneda' is an exported plain string — hidden on Deudores, and typed
 *    `currency` on the dues sets, where it rendered as "$U UYU";
 *  - the headline amount splits into a USD and a UYU column when the report is
 *    not filtered down to a single currency.
 *
 * Columns the shared sets flagged `toExport: true` are omitted here: they never
 * reached the xlsx anyway.
 */

const SPLIT_CURRENCIES = ['USD', 'UYU'];

/** Mixed-currency rows need one amount column per currency to be summable. */
export function shouldSplitAmountByCurrency(filterOptions = {}) {
	return !SPLIT_CURRENCIES.includes(filterOptions.currency);
}

/**
 * The report's headline amount. `currencyOf` says where a row's currency lives
 * — top level on debts, on the parent debt/credit for a due.
 */
function amountColumns({ key, labelKey, currencyOf, split }) {
	if (!split) {
		return [{ [key]: { type: 'amount', get label() { return getLabel(labelKey); } } }];
	}
	// Virtual columns: `value` resolves the cell itself (see reportColumnAdapter),
	// leaving the non-matching currency blank so SUM() stays correct.
	return SPLIT_CURRENCIES.map((code) => ({
		[`${key}${code}`]: {
			type: 'amount',
			get label() { return `${getLabel(labelKey)} ${code}`; },
			value: (item) => (currencyOf(item) === code ? item[key] ?? '' : ''),
		},
	}));
}

function debtsReportColumns(split) {
	return [
		{ customId: { type: 'string', get label() { return getLabel('field.accountConcept'); } } },
		{ 'owner.name': { type: 'string', get label() { return getLabel('entity.account.debtor'); } } },
		{ currency: { type: 'string', get label() { return getLabel('field.currency'); } } },
		{ capital: { type: 'amount', get label() { return getLabel('field.capital'); } } },
		{ concept: { type: 'string', get label() { return getLabel('entity.concept'); } } },
		{ dues: { type: 'number', get label() { return getLabel('entity.due.plural'); } } },
		...amountColumns({ key: 'amount', labelKey: 'field.dueAmount', currencyOf: (item) => item.currency, split }),
		{ date: { type: 'date', get label() { return getLabel('field.date'); } } },
		{ endDate: { type: 'date', get label() { return getLabel('field.endDate'); } } },
		{ periodicity: { type: 'string', get label() { return getLabel('field.periodicity'); } } },
		{ lastPaymentDate: { type: 'date', get label() { return getLabel('field.lastPaymentDate'); }, forReport: true } },
	];
}

function debtDuesReportColumns(split) {
	return [
		{ customId: { type: 'string', get label() { return getLabel('entity.due'); } } },
		{ 'debt.owner.name': { type: 'string', get label() { return getLabel('entity.account.debtor'); } } },
		{ 'debt.concept': { type: 'string', get label() { return getLabel('entity.concept'); } } },
		{ date: { type: 'date', get label() { return getLabel('field.date'); } } },
		{ interest: { type: 'amount', get label() { return getLabel('field.interest'); } } },
		{ 'debt.currency': { type: 'string', get label() { return getLabel('field.currency'); } } },
		{ amortization: { type: 'amount', get label() { return getLabel('field.amortization'); } } },
		...amountColumns({ key: 'amount', labelKey: 'field.amount', currencyOf: (item) => item.debt?.currency, split }),
		{ expiration: { type: 'date', get label() { return getLabel('field.expiration'); } } },
		{ balance: { type: 'amount', get label() { return getLabel('field.balance'); } } },
		{ surcharge: { type: 'amount', get label() { return getLabel('field.surcharge'); } } },
		{ unpaidAmount: { type: 'amount', get label() { return getLabel('field.unpaidAmount'); } } },
		{ paid: { type: 'boolean', get label() { return getLabel('field.paid'); } } },
		{ status: { type: 'string', get label() { return getLabel('field.status'); } } },
		{ lastPaymentDate: { type: 'date', get label() { return getLabel('field.lastPaymentDate'); }, forReport: true } },
	];
}

function creditDuesReportColumns(split) {
	return [
		{ customId: { type: 'string', get label() { return getLabel('entity.due'); } } },
		{ 'credit.owner.name': { type: 'string', get label() { return getLabel('entity.account.creditor'); } } },
		{ date: { type: 'date', get label() { return getLabel('field.date'); } } },
		{ interest: { type: 'amount', get label() { return getLabel('field.interest'); } } },
		{ 'credit.currency': { type: 'string', get label() { return getLabel('field.currency'); } } },
		{ amortization: { type: 'amount', get label() { return getLabel('field.amortization'); } } },
		...amountColumns({ key: 'amount', labelKey: 'field.amount', currencyOf: (item) => item.credit?.currency, split }),
		{ expiration: { type: 'date', get label() { return getLabel('field.expiration'); } } },
		{ unpaidAmount: { type: 'amount', get label() { return getLabel('field.unpaidAmount'); } } },
		{ paid: { type: 'boolean', get label() { return getLabel('field.paid'); } } },
	];
}

// A payment's own currency is optional; fall back to the debt it pays.
const paymentCurrency = (item) => item.currency || item.debt?.currency;

function paymentsReportColumns(split) {
	return [
		{ customId: { type: 'string', get label() { return getLabel('field.id'); } } },
		{ 'debtDue.customId': { type: 'string', get label() { return getLabel('entity.due'); } } },
		{ currency: { type: 'string', get label() { return getLabel('field.currency'); }, value: (item) => paymentCurrency(item) || 'N/A' } },
		...amountColumns({ key: 'amount', labelKey: 'field.amount', currencyOf: paymentCurrency, split }),
		{ date: { type: 'date', get label() { return getLabel('field.date'); } } },
		{ folder: { type: 'string', get label() { return getLabel('field.folder'); } } },
		{ status: { type: 'string', get label() { return getLabel('field.status'); } } },
	];
}

function assetSalesReportColumns(split) {
	return [
		{ customId: { type: 'string', get label() { return getLabel('field.id'); } } },
		{ date: { type: 'date', get label() { return getLabel('field.date'); } } },
		{ lot: { type: 'string', get label() { return getLabel('field.lotUpper'); } } },
		{ currency: { type: 'string', get label() { return getLabel('field.currency'); } } },
		...amountColumns({ key: 'amount', labelKey: 'field.amount', currencyOf: (item) => item.currency, split }),
		{ 'purchaser.name': { type: 'string', get label() { return getLabel('entity.purchaser'); } } },
		{ sumDebts: { type: 'amount', get label() { return getLabel('field.totalDebts'); } } },
		{ initialPayment: { type: 'amount', get label() { return getLabel('field.initialPayment'); } } },
		{ 'asset.name': { type: 'string', get label() { return getLabel('field.property'); }, forReport: true } },
	];
}

const COLUMN_BUILDERS = {
	Deudas: debtsReportColumns,
	CuotasDeudas: debtDuesReportColumns,
	CuotasCreditos: creditDuesReportColumns,
	Cobros: paymentsReportColumns,
	VentasActivo: assetSalesReportColumns,
};

export function buildReportColumns(formType, filterOptions = {}) {
	return COLUMN_BUILDERS[formType](shouldSplitAmountByCurrency(filterOptions));
}
